



















































































































import Vue from "vue";
import eventBus from "@/services/eventBus";
import base64toUInt8Array, { openPdfInNewTab } from "@/utils/fileConvert";
import { SortDescriptor, orderBy } from "@progress/kendo-data-query";
import store, { GeneralLedgerStore } from "../services/store";
import api from "../services/api";
import LookupComboBox from "@/modules/transactions/components/LookupComboBox.vue";
import { Dimension } from "../models/Dimension";
import {
  DimensionListReportFilter,
  DefaultDimensionListReportFilter,
  DimensionStatusOption,
  AllStatusesOption,
} from "../models/DimensionListReportFilter";

interface TypeOption {
  id: number;
  national: string;
}

interface Data {
  store: GeneralLedgerStore;
  filter: DimensionListReportFilter;
  items: Dimension[];
  dimcodeOptions: Dimension[];
  columns: any[];
  statusOptions: DimensionStatusOption[];
  sort: SortDescriptor[];
  generating: boolean;
}
interface Methods {
  loadStatuses(): void;
  loadList(): void;
  loadDimcodeOptions(): void;
  setFromDimcode(e: any): void;
  setToDimcode(e: any): void;
  extractDimcode(e: any): string;
  generatePdf(): void;
  reset(): void;
  sortChangeHandler(e: any): void;
}
interface Computed {
  displayedItems: Dimension[];
  typeOptions: TypeOption[];
}

export default Vue.extend<Data, Methods, Computed, {}>({
  components: { LookupComboBox },

  data(): Data {
    return {
      store,
      filter: { ...DefaultDimensionListReportFilter },
      items: [],
      dimcodeOptions: [],
      statusOptions: [AllStatusesOption],
      sort: [],
      generating: false,
      columns: [
        { field: "dimcode", title: "Dimenzió kód", width: "160px" },
        { field: "national", title: "Megnevezés" },
        { field: "type", title: "Típus", width: "160px" },
        { field: "status", title: "Állapot", width: "160px" },
        { field: "activity", title: "Tevékenység típusa", width: "180px" },
        { field: "absorption", title: "Abszorpciós ráta %", width: "150px", className: "right_text" },
      ],
    };
  },

  async beforeMount() {
    await Promise.all([
      this.store.setYears(),
      this.store.setDimCodeTypes(),
      this.loadStatuses(),
    ]);
    if (this.store.years.length > 0) {
      // K2D: gl_Year = a mai naphoz tartozó könyvelési év (w_sheet_trans open), nem a legújabb
      const today = new Date().toLocaleDateString("sv-SE");
      const toIso = (d: unknown) => (typeof d === "string" ? d.slice(0, 10) : "");
      const currentYear =
        this.store.years.find(y => toIso(y.startDate) !== "" && toIso(y.startDate) <= today && today <= toIso(y.endDate)) ??
        this.store.years.find(y => y.name == String(new Date().getFullYear()));
      this.filter.yearId = (currentYear ?? this.store.years[0]).id;
    }
  },

  computed: {
    typeOptions(): TypeOption[] {
      return [{ id: 0, national: "Összes" }, ...this.store.dimCodeTypes];
    },
    displayedItems(): Dimension[] {
      return orderBy(this.items, this.sort);
    },
  },

  methods: {
    async loadStatuses() {
      const statuses = await api.GetDimensionListStatuses();
      this.statusOptions = [AllStatusesOption, ...statuses];
    },

    async loadDimcodeOptions() {
      if (!this.filter.yearId) {
        this.dimcodeOptions = [];
        return;
      }
      this.dimcodeOptions = await api.SearchDimensionListReport({
        yearId: this.filter.yearId,
        fromDimcode: "",
        toDimcode: "",
        statusId: 0,
        typeId: 0,
      });
    },

    extractDimcode(e): string {
      if (!e) return "";
      if (typeof e === "string") return e;
      return e.dimcode ?? "";
    },

    // K2D w_popup_report_mirror.srw:215-220 (b_from_accno): a Tól-lookupban választott kód
    // a Ig mezőbe IS átmásolódik (a Ig külön is felülírható utána).
    setFromDimcode(e) {
      const value = this.extractDimcode(e);
      this.filter.fromDimcode = value;
      this.filter.toDimcode = value;
    },

    setToDimcode(e) {
      this.filter.toDimcode = this.extractDimcode(e);
    },

    async loadList() {
      if (!this.filter.yearId) {
        eventBus.$emit("error", "Válasszon könyvelési évet!");
        return;
      }
      this.items = await api.SearchDimensionListReport(this.filter);
    },

    async generatePdf() {
      if (!this.filter.yearId) {
        eventBus.$emit("error", "Válasszon könyvelési évet!");
        return;
      }
      this.generating = true;
      // Egész oldalas töltésjelző a Crystal-riport elkészültéig (BusyIndicator overlay)
      eventBus.$emit("busy-message", true);
      try {
        const base64 = await api.GenerateDimensionListReportPdf(this.filter);
        if (!base64) {
          eventBus.$emit("error", "A riport generálása nem sikerült!");
          return;
        }
        openPdfInNewTab(base64toUInt8Array(base64));
      } catch (error) {
        eventBus.$emit("error", "A riport generálása nem sikerült!");
        console.error(error);
      } finally {
        eventBus.$emit("busy-message", false);
        this.generating = false;
      }
    },

    reset() {
      const yearId = this.filter.yearId;
      this.filter = { ...DefaultDimensionListReportFilter, yearId };
      this.items = [];
      this.sort = [];
    },

    sortChangeHandler(e) {
      this.sort = e.sort;
    },
  },

  watch: {
    "filter.yearId"() {
      this.filter.fromDimcode = "";
      this.filter.toDimcode = "";
      this.loadDimcodeOptions();
    },
  },
});
